import { type NuxtAxiosInstance } from '@/types/axios'
import { adaptObjectToSearchParams } from '@/utils/apiAdapter'
import type {
  ArrivalTimesResult,
  BranchArrivalTimesQS,
  BranchHeadcountsQS,
  BranchListQS,
  BranchProductsQS,
  HeadcountsResult,
  ProductsResult,
} from '@/view-models/branch'
import { ResponseCode, type ResultGenericVM, type ResultListGenericVM } from '@/view-models/common'
import type { BranchListBranchData, BranchReviewSummaryData, BranchPAAData } from '@/types/api'

export async function getBranchList(
  axios: NuxtAxiosInstance,
  { size = 10, filter_times, ...params }: BranchListQS
): Promise<ResultListGenericVM<BranchListBranchData>> {
  const query = {
    ...params,
    size,
    ...(filter_times?.length && { 'filter_times[]': filter_times }),
  }
  const res = await axios.$get('/pub/branch', { params: query })

  if (res.code !== 0) {
    throw res
  }

  return res
}

/**
 * @doc https://github.com/funnow-docs/funnow-api-doc/blob/6f9a4445a7dafe327c14a3d80ad0e07e3e0c3002/docs/branch/arrivaltime.md
 */
export async function getArrivalTimes(
  axios: NuxtAxiosInstance,
  bid: number,
  params: BranchArrivalTimesQS
): Promise<ResultGenericVM<ArrivalTimesResult>> {
  if (!bid) {
    throw new Error('branch id cant be empty')
  }

  const res = await axios.$get<ResultGenericVM<ArrivalTimesResult>>(`/pub/branch/${bid}/arrivaltimes?${adaptObjectToSearchParams(params).toString()}`)
  if (res.code !== 0) {
    throw res
  }

  return res
}

/**
 * @doc https://github.com/funnow-docs/funnow-api-doc/blob/6f9a4445a7dafe327c14a3d80ad0e07e3e0c3002/docs/branch/headcount.md
 */
export async function getHeadcounts(axios: NuxtAxiosInstance, bid: number, params: BranchHeadcountsQS): Promise<ResultGenericVM<HeadcountsResult>> {
  if (!bid) {
    throw new Error('branch id cant be empty')
  }

  const res = await axios.$get(`/pub/branch/${bid}/headcount?${adaptObjectToSearchParams(params).toString()}`)

  if (res.code !== 0) {
    throw res
  }

  return res
}

/**
 * @doc https://github.com/funnow-docs/funnow-api-doc/blob/6f9a4445a7dafe327c14a3d80ad0e07e3e0c3002/docs/branch/headcount.md
 */
export async function getProducts(axios: NuxtAxiosInstance, bid: number, params: BranchProductsQS): Promise<ResultGenericVM<ProductsResult[]>> {
  if (!bid) {
    throw new Error('branch id cant be empty')
  }

  const res = await axios.$get(`/pub/branch/${bid}/products?${adaptObjectToSearchParams({ ...params, size: 99 }).toString()}`)

  if (res.code !== 0) {
    throw res
  }

  return res
}

export async function getReviewSummary(axios: NuxtAxiosInstance, bid: number | string): Promise<ResultGenericVM<BranchReviewSummaryData>> {
  if (!bid) {
    throw new Error('branch id cant be empty')
  }

  const res = await axios.$get<ResultGenericVM<BranchReviewSummaryData>>(`/pub/branch/${bid}/review-summary`)

  if (res.code !== 0) {
    throw res
  }

  return res
}

export async function getBranchPAA(axios: NuxtAxiosInstance, bid: number): Promise<ResultGenericVM<BranchPAAData>> {
  const res = await axios.$get(`/pub/branch/${bid}/paa`)

  if (res.code !== ResponseCode.Success) {
    throw res
  }

  return res
}
